import React, { useEffect, useRef, useState } from 'react';
import styles from './ScrollFilm.module.css';

/** Bounded, near-viewport frame loading. No video seek stalls on mobile Safari. */
export default function ScrollFilm({ basePath, frameCount, poster, alt, rangeRef, reducedMotion, cover = false, background = '#080b0c' }) {
  const hostRef = useRef(null), canvasRef = useRef(null);
  const [painted, setPainted] = useState(false);
  useEffect(() => {
    const host = hostRef.current, canvas = canvasRef.current;
    if (!host || !canvas || reducedMotion) { setPainted(false); return undefined; }
    let cancelled = false, active = false, started = false, raf = 0;
    let target = 0, displayed = 0, lastPaint = -1;
    const images = new Map(), pending = new Map(), failed = new Set();
    const framePath = window.matchMedia('(max-width: 700px)').matches ? `${basePath}/mobile` : basePath;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) return undefined;
    const paint = () => {
      raf = 0;
      if (cancelled || !active || document.hidden) return;
      displayed += (target - displayed) * .22;
      if (Math.abs(displayed - target) < .008) displayed = target;
      const wanted = Math.round(displayed * (frameCount - 1));
      let nearest = -1, distance = Infinity;
      images.forEach((_, i) => { if (Math.abs(i - wanted) < distance) { nearest = i; distance = Math.abs(i - wanted); } });
      const image = images.get(nearest);
      if (image && canvas.width && nearest !== lastPaint) {
        const fit = cover ? Math.max : Math.min;
        const scale = fit(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
        const w = image.naturalWidth * scale, h = image.naturalHeight * scale;
        context.fillStyle = background; context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
        lastPaint = nearest; host.dataset.frame = String(nearest);
        host.dataset.loadedFrames = String(images.size); setPainted(true);
      }
      if (Math.abs(target - displayed) > .008) schedule();
    };
    const schedule = () => { if (!raf && !cancelled) raf = requestAnimationFrame(paint); };
    const update = () => {
      const range = rangeRef?.current;
      if (range) {
        const r = range.getBoundingClientRect();
        target = Math.max(0, Math.min(1, -r.top / Math.max(1, r.height - window.innerHeight)));
      } else {
        const r = host.getBoundingClientRect();
        target = Math.max(0, Math.min(1, (window.innerHeight - r.top) / (window.innerHeight + r.height)));
      }
      schedule(); loadAround();
    };
    const loadAround = () => {
      if (cancelled || !started) return;
      const center = Math.round(target * (frameCount - 1));
      // At most 25 decoded images per film; the browser caches compressed bytes.
      images.forEach((_, index) => { if (Math.abs(index - center) > 12) images.delete(index); });
      const queue = [center];
      for (let d = 1; d <= 12; d++) queue.push(center + d, center - d);
      for (const index of queue) {
        if (pending.size >= 4) break;
        if (index < 0 || index >= frameCount || images.has(index) || pending.has(index) || failed.has(index)) continue;
        const image = new Image(); pending.set(index, image); image.decoding = 'async';
        image.onload = () => {
          pending.delete(index); if (cancelled) return;
          images.set(index, image); lastPaint = -1; schedule(); loadAround();
        };
        image.onerror = () => { pending.delete(index); failed.add(index); if (!cancelled) loadAround(); };
        image.src = `${framePath}/f-${String(index + 1).padStart(3, '0')}.webp`;
      }
    };
    // Four concurrent decodes, capped at the exported frame count, only near the film.
    const preloadObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || started) return;
      started = true;
      loadAround();
      preloadObserver.disconnect();
    }, { rootMargin: '500px' });
    preloadObserver.observe(host);
    const visibility = new IntersectionObserver(([entry]) => {
      active = entry.isIntersecting;
      if (active) update(); else { cancelAnimationFrame(raf); raf = 0; }
    });
    visibility.observe(host);
    const resize = () => {
      const r = host.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(r.width * dpr); canvas.height = Math.round(r.height * dpr);
      lastPaint = -1; update();
    };
    const sizeObserver = new ResizeObserver(resize); sizeObserver.observe(host);
    const onVisibility = () => { if (!document.hidden) update(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('scroll', update, { passive: true });
    resize();
    return () => {
      cancelled = true; cancelAnimationFrame(raf);
      preloadObserver.disconnect(); visibility.disconnect(); sizeObserver.disconnect();
      window.removeEventListener('scroll', update); document.removeEventListener('visibilitychange', onVisibility);
      pending.forEach(image => { image.onload = null; image.onerror = null; image.src = ''; });
      images.clear(); pending.clear();
    };
  }, [basePath, frameCount, rangeRef, reducedMotion, cover, background]);
  return <div ref={hostRef} className={styles.film} style={{ background }} role="img" aria-label={alt}>
    <img src={poster} alt="" loading="lazy" width="1920" height="1080"
      className={`${styles.poster} ${cover ? styles.cover : ''} ${painted && !reducedMotion ? styles.hidden : ''}`} />
    <canvas ref={canvasRef} aria-hidden="true" />
  </div>;
}
